import classnames from 'classnames/bind'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { WbProductVariant } from '~/@types/product'
import { SHOPIFY_VARIANT_SELECTED_OPTION_SIZES_NAME } from '~/lib/constants'

import { ProductHeaderProps } from '~/components/UI/ProductHeader'
import { ProductCustomState } from '~/components/UI/ProductHeader/ProductDetails'
import ProductDetailsBottomInformations from '~/components/UI/ProductHeader/ProductDetailsBottomInformations'
import ProductSliderSelect from '~/components/UI/ProductHeader/ProductSliderSelect'
import ProductVariationsSelect from '~/components/UI/ProductHeader/ProductVariationsSelect'

import { isSizeOrDefaultVariantName } from '~/utils/is-size-or-default-variant-name'
import { getVariantFromSelectedOptions } from '~/utils/variant-helpers'

import { ProductProps } from '~/data/global-data'

import css from './styles.module.scss'

const ProductSizeGuide = dynamic(
  () => import('~/components/UI/ProductHeader/ProductSizeGuide'),
)

const cx = classnames.bind(css)

export type ProductHeaderVariantsProps = {
  className?: string
  variants?: ProductHeaderProps['variants']
  theme?: string
  selectedVariant?: ProductHeaderProps['selectedVariant']
  setSelectedVariant?: ProductHeaderProps['setSelectedVariant']
  isQuickbuy?: boolean
  hasSizeGuide?: boolean
  isAdjusting?: ProductHeaderProps['isAdjusting']
  isStockAlert?: ProductHeaderProps['isStockAlert']
  isSpecialOrder?: ProductHeaderProps['isSpecialOrder']
  adjustableOffset?: ProductHeaderProps['adjustableOffset']
  preOrder?: ProductHeaderProps['preOrder']
  notes?: ProductProps['notes']
  customProductState: ProductCustomState
  setCustomProductState: Dispatch<SetStateAction<ProductCustomState>>
  productVariations?: ProductHeaderProps['productVariations']
  sizeGuide?: ProductHeaderProps['sizeGuide']
}

function ProductHeaderVariants({
  className,
  notes,
  variants,
  selectedVariant,
  hasSizeGuide,
  setSelectedVariant,
  isStockAlert,
  isSpecialOrder,
  isAdjusting,
  preOrder,
  productVariations,
  sizeGuide,
  setCustomProductState,
}: ProductHeaderVariantsProps) {
  const router = useRouter()
  const [isSizeGuideVisible, setIsSizeGuideVisible] = useState(false)
  const selectedVariantAvailableForSale =
    selectedVariant?.availableForSale && !selectedVariant?.currentlyNotInStock
  const selectedVariantOutOfStock =
    !selectedVariantAvailableForSale && !preOrder?.isActive

  const options =
    variants?.reduce((acc, variant) => {
      variant?.selectedOptions?.forEach((option) => {
        if (!acc[option.name]) {
          acc[option.name] = []
        }

        const selectedOptions = [
          ...(selectedVariant?.selectedOptions?.filter?.(
            (selectedOption) => selectedOption.name !== option.name,
          ) ?? []),
          option,
        ]
        const variant = getVariantFromSelectedOptions(selectedOptions, variants)

        if (
          variant &&
          !acc[option.name].find(
            (v: WbProductVariant) =>
              v.id === variant.id &&
              (isAdjusting ? v.title === variant.title : true),
          )
        ) {
          acc[option.name].push({
            ...variant,
            title: option.value,
          })
        }
      })
      return acc
    }, {}) ?? {}
  const variantKeys = Object.keys(options)

  const hasSizeVariant =
    variantKeys?.filter((key) => {
      return isSizeOrDefaultVariantName(key)
    })?.length > 0 ?? false

  const getUniqueVariantSize = () => {
    if (!isUniqueVariant) return null
    const size = variants?.[0]?.selectedOptions?.find((option) =>
      SHOPIFY_VARIANT_SELECTED_OPTION_SIZES_NAME?.includes(
        option?.name?.toUpperCase(),
      ),
    )?.value

    return !isNaN(parseInt(size)) ? size : null
  }

  const isUniqueVariant = variants?.length <= 1
  const isThreeSizesVariants = hasSizeVariant && variants?.length === 3

  const onChangeVariant = (id, title) => {
    const targetVariant = variants?.find(
      (variant) =>
        variant?.id === id && (isAdjusting ? variant?.title === title : true),
    )

    setSelectedVariant(targetVariant)

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router?.query,
          variant: targetVariant.title,
        },
      },
      undefined,
      { shallow: true },
    )
  }

  const onClickSizeGuide = () => {
    setIsSizeGuideVisible(true)
  }

  const onCloseSizeGuide = () => {
    setIsSizeGuideVisible(false)
  }

  useEffect(() => {
    if (hasSizeVariant) {
      if (isAdjusting) {
        if (
          selectedVariant?.isVariantAdjusting &&
          selectedVariant?.isVariantSellable
        ) {
          setCustomProductState((prevValue) => ({
            ...prevValue,
            isAdjusting: true,
          }))
        } else {
          setCustomProductState((prevValue) => ({
            ...prevValue,
            isAdjusting: false,
          }))
        }
      }

      setCustomProductState((prevValue) => ({
        ...prevValue,
        selectedSize: selectedVariant?.selectedOptions?.find((option) =>
          isSizeOrDefaultVariantName(option.name),
        )?.value,
      }))
    }

    if (preOrder) {
      setCustomProductState((prevValue) => ({
        ...prevValue,
        isPreOrder: preOrder?.isActive,
      }))
    }

    if (isUniqueVariant) {
      setCustomProductState((prevValue) => ({
        ...prevValue,
        isUniqueSize: true,
      }))
    }
  }, [selectedVariant, hasSizeVariant, isUniqueVariant, isAdjusting, preOrder])

  return (
    <div className={cx(className, css.ProductHeaderVariants)}>
      {variants?.length > 1 &&
        variantKeys?.map((key) => {
          const isSize = isSizeOrDefaultVariantName(key)

          const selectVariants = options[key]

          const selectOptions = selectVariants?.map((variant) => {
            const variantAvailableForSale =
              variant?.availableForSale && !variant?.currentlyNotInStock
            const variantOutOfStock =
              !isAdjusting && !variantAvailableForSale && !preOrder?.isActive

            return {
              label: `${variant?.title}${
                isSize && variant?.isVariantAdjusting ? `*` : ``
              }`,
              value: variant?.id,
              selected:
                variant?.id === selectedVariant?.id &&
                (isAdjusting
                  ? variant?.title === selectedVariant?.title
                  : true),
              disabled: isSize && !variantAvailableForSale,
              isOutOfStock: variantOutOfStock,
            }
          })

          return isSize ? (
            <ProductSliderSelect
              theme={'large'}
              className={css.sizeSelect}
              items={selectOptions}
              onChange={(id, value) => {
                const processedValue = value?.replace('*', '')

                onChangeVariant(id, processedValue)
              }}
              key={`size_select_${key}`}></ProductSliderSelect>
          ) : null
        })}
      {productVariations && (
        <ProductVariationsSelect
          {...productVariations}
          className={css.productVariations}
          mainCss={css}
        />
      )}

      {hasSizeGuide && hasSizeVariant && (
        <ProductSizeGuide
          className={css.productSizeGuide}
          isSizeGuideVisible={isSizeGuideVisible}
          onClose={() => {
            onCloseSizeGuide()
          }}
          {...sizeGuide}
        />
      )}

      <ProductDetailsBottomInformations
        className={cx(css.bottomInformations, {
          hasVariants: variants?.length > 1,
        })}
        hasSizeGuide={hasSizeGuide}
        hasSizeVariant={hasSizeVariant}
        preOrder={preOrder}
        isUniqueVariant={isUniqueVariant}
        isThreeSizesVariants={isThreeSizesVariants}
        isVariantAdjusting={selectedVariant?.isVariantAdjusting}
        isVariantSellable={selectedVariant?.isVariantSellable}
        isAdjusting={isAdjusting}
        isSpecialOrder={isSpecialOrder}
        isStockAlert={isStockAlert}
        isOutOfStock={selectedVariantOutOfStock}
        onClickSizeGuide={onClickSizeGuide}
        notes={notes}
        uniqueVariantSize={getUniqueVariantSize()}
      />
    </div>
  )
}

ProductHeaderVariants.defaultProps = {}

export default ProductHeaderVariants
